import { defineStore } from "pinia";
import Network from "~/utils/network";
import { useRoute } from "vue-router";
import { useSessionStore } from "./session";

const urls = {
  myLimit: `/bnpl/limit/my-limit`,
  myLastLimit: `/bnpl/limit/my-last-limit`,
  create: `/bnpl/limit/create`,
  updateFlowPosition: (limitId) => `/bnpl/limit/update-flow-position/${limitId}`,
  cancelLimit: (limitId) => `/bnpl/limit/cancel/${limitId}`,
  getScoringQuestions: `/bnpl/limit/scoring-questions`,
  saveDynamicData: `/bnpl/limit/save-dynamic-data`,
  scoringEvaluate: `/bnpl/limit/scoring-evaluate`,
  scoringEvaluateByPublicId: (publicId) => `/bnpl/limit/scoring-evaluate/${publicId}`,
  existLiveLimits: `/bnpl/limit/exist-live-limits`,
};

const nextPosition = (position) => {
  switch (position) {
    case LIMIT_POSITIONS.INTRO:
      return LIMIT_POSITIONS.DATA;
    case LIMIT_POSITIONS.DATA:
      return LIMIT_POSITIONS.EMAIL_VERIFICATION;
    case LIMIT_POSITIONS.EMAIL_VERIFICATION:
      return LIMIT_POSITIONS.PSD2_SYNC;
    case LIMIT_POSITIONS.PSD2_SYNC:
      return LIMIT_POSITIONS.SCORING;
    case LIMIT_POSITIONS.SCORING:
      return LIMIT_POSITIONS.RESULT;
  }
};

const previousPosition = (position) => {
  switch (position) {
    case LIMIT_POSITIONS.DATA:
      return LIMIT_POSITIONS.INTRO;
    case LIMIT_POSITIONS.EMAIL_VERIFICATION:
      return LIMIT_POSITIONS.DATA;
    case LIMIT_POSITIONS.PSD2_SYNC:
      return LIMIT_POSITIONS.EMAIL_VERIFICATION;
    case LIMIT_POSITIONS.SCORING:
      return LIMIT_POSITIONS.PSD2_SYNC;
  }
};

const positionNumber = (position) => {
  switch (position) {
    case LIMIT_POSITIONS.INTRO:
      return 1;
    case LIMIT_POSITIONS.DATA:
      return 2;
    case LIMIT_POSITIONS.EMAIL_VERIFICATION:
      return 3;
    case LIMIT_POSITIONS.PSD2_SYNC:
      return 4;
    case LIMIT_POSITIONS.SCORING:
      return 5;
    case LIMIT_POSITIONS.RESULT:
      return 6;
    default:
      return 0;
  }
};

export const useLimitStore = defineStore("limit", {
  state: () => ({
    limit: {},
    scoringQuestions: [],
    source: "",
    createInProgress: false,
  }),

  actions: {
    fetchMyLimit: async function () {
      try {
        const data = await Network.http(urls.myLimit, {
          method: "GET",
        });
        this.limit = data;
      } catch (err) {
        if (err.status !== 404) {
          console.log(err);
          await Promise.reject(err);
        } else {
          console.log("no limit found");
          await Promise.reject(err);
        }
      }
    },
    fetchMyLastLimit: async function () {
      try {
        const data = await Network.http(urls.myLastLimit, {
          method: "GET",
        });
        this.limit = data;
      } catch (err) {
        if (err.status !== 404) {
          console.log(err);
          await Promise.reject(err);
        } else {
          console.log("no limit found");
          await Promise.reject(err);
        }
      }
    },
    createLimit: async function (source) {
      if (!this.createInProgress) {
        this.createInProgress = true;
        try {
          const data = await Network.http(urls.create, {
            method: "POST",
            body: {
              source,
            },
          });
          this.limit = data;
          this.createInProgress = false;
        } catch (err) {
          this.createInProgress = false;
          console.log(err);
          await Promise.reject(err);
        }
      }
    },
    nextPosition: async function () {
      let flowPosition = nextPosition(this.limit.flowPosition);
      const email = useSessionStore().email;
      if (this.limit.flowPosition === LIMIT_POSITIONS.EMAIL_VERIFICATION && email) {
        flowPosition = nextPosition(this.limit.flowPosition);
      }
      try {
        const data = await Network.http(urls.updateFlowPosition(this.limit.id), {
          method: "PUT",
          body: {
            flowPosition: flowPosition,
          },
        });
        this.limit = data;
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    previousPosition: async function () {
      let flowPosition = previousPosition(this.limit.flowPosition);
      const email = useSessionStore().email;
      if (this.limit.flowPosition === LIMIT_POSITIONS.EMAIL_VERIFICATION && email) {
        flowPosition = previousPosition(this.limit.flowPosition);
      }
      try {
        const data = await Network.http(urls.updateFlowPosition(this.limit.id), {
          method: "PUT",
          body: {
            flowPosition: flowPosition,
          },
        });
        this.limit = data;
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    getScoringQuestions: async function () {
      try {
        const data = await Network.http(urls.getScoringQuestions, {
          method: "GET",
        });
        this.scoringQuestions = data;
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    saveDynamicData: async function (dynamicData) {
      try {
        await Network.http(urls.saveDynamicData, {
          method: "PUT",
          body: {
            ...dynamicData,
          },
        });
        this.fetchMyLimit();
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    scoringEvaluate: async function () {
      try {
        await Network.http(urls.scoringEvaluate, {
          method: "POST",
          body: {
            dynamicData: this.limit.dynamicData,
          },
        });
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    scoringEvaluateByPublicId: async function (publicId) {
      try {
        await Network.http(urls.scoringEvaluateByPublicId(publicId), {
          method: "POST",
          body: {},
        });
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    cancelLimit: async function (limitId) {
      try {
        await Network.http(urls.cancelLimit(limitId), {
          method: "PUT",
        });
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    hasLiveLimits: async function () {
      try {
        return await Network.http(urls.existLiveLimits, {
          method: "GET",
        });
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
  },

  getters: {
    getPositionNumber() {
      return positionNumber(this.limit.flowPosition);
    },
  },
});
